import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, TextField, Switch, CircularProgress, Alert, Button } from '@mui/material';
import { CheckCircleOutline as CheckCircleIcon, DoNotDisturb as NoEntryIcon, Sync as SyncIcon } from '@mui/icons-material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { VALIDATE_MEETING_PACKAGE_VENUE_QUERY, SYNC_MEETING_PACKAGE_VENUE_MUTATION, DISABLE_MEETING_PACKAGE_VENUE_MUTATION } from './gql';

interface HotelMeetingPackageTabProps {
  hotelId: number;
  spaceId: number;
  propertyIdentifier?: string | null;
  hotelName: string;
}

const HotelMeetingPackageTab: React.FC<HotelMeetingPackageTabProps> = ({ hotelId, spaceId, propertyIdentifier, hotelName }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [venueId, setVenueId] = useState('');
  const [validatedName, setValidatedName] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isEnabled = !!propertyIdentifier;

  const [validateVenue] = useLazyQuery(VALIDATE_MEETING_PACKAGE_VENUE_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [syncVenue, { loading: syncing }] = useMutation(SYNC_MEETING_PACKAGE_VENUE_MUTATION);
  const [disableVenue, { loading: deleting }] = useMutation(DISABLE_MEETING_PACKAGE_VENUE_MUTATION);

  useEffect(() => {
    const mpVenueId = propertyIdentifier || '';
    setVenueId(mpVenueId);
    if (mpVenueId) {
      setValidatedName(hotelName);
      setHasSearched(true);
    }
  }, [hotelName, propertyIdentifier]);

  const runValidation = async (value: string) => {
    if (!value.trim()) {
      setValidatedName(null);
      setHasSearched(false);
      return;
    }
    setIsValidating(true);
    setHasSearched(true);
    try {
      const res = await validateVenue({ variables: { spaceId, venueId: value.trim() } });
      const venue = res.data?.validateMeetingPackageVenue;
      setValidatedName(venue?.name ?? null);
    } catch {
      setValidatedName(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleVenueIdChange = (value: string) => {
    setVenueId(value);
    setValidatedName(null);
    setHasSearched(false);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runValidation(value), 800);
  };

  const handleToggle = async () => {
    if (isEnabled) {
      try {
        await disableVenue({ variables: { hotelId } });
        dispatchMessage(dispatch, i18next.t('hotel-mp-disabled'));
        navigate(0);
      } catch (err) {
        dispatchException(dispatch, err);
      }
    } else {
      if (!validatedName) return;
      try {
        await syncVenue({
          variables: { hotelId, venueId: venueId.trim() },
        });
        dispatchMessage(dispatch, i18next.t('hotel-mp-enabled'));
        navigate(0);
      } catch (err) {
        dispatchException(dispatch, err);
      }
    }
  };

  const handleSync = async () => {
    if (!propertyIdentifier) return;
    try {
      await syncVenue({ variables: { hotelId, venueId: propertyIdentifier } });
      dispatchMessage(dispatch, i18next.t('hotel-mp-synced'));
      navigate(0);
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const canEnable = !!validatedName && !isEnabled;
  const isToggleBusy = syncing || deleting;

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', mt: 2 }}>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: '#fce4ec',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="3" fill="#c2185b" />
              <circle cx="12" cy="4" r="2" fill="#c2185b" />
              <circle cx="12" cy="20" r="2" fill="#c2185b" />
              <circle cx="4" cy="12" r="2" fill="#c2185b" />
              <circle cx="20" cy="12" r="2" fill="#c2185b" />
              <line x1="12" y1="6" x2="12" y2="9" stroke="#c2185b" strokeWidth="1.5" />
              <line x1="12" y1="15" x2="12" y2="18" stroke="#c2185b" strokeWidth="1.5" />
              <line x1="6" y1="12" x2="9" y2="12" stroke="#c2185b" strokeWidth="1.5" />
              <line x1="15" y1="12" x2="18" y2="12" stroke="#c2185b" strokeWidth="1.5" />
            </svg>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight={600}>
                Meeting Package
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary' }}>
                  {i18next.t('hotel-mp-enable-service').toUpperCase()}
                </Typography>
                {isToggleBusy ? (
                  <CircularProgress size={20} />
                ) : (
                  <Switch checked={isEnabled} onChange={handleToggle} disabled={!isEnabled && !canEnable} color="primary" />
                )}
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {i18next.t('hotel-mp-description')}
            </Typography>
          </Box>
        </Box>

        {!isEnabled && (
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                {i18next.t('hotel-mp-property-identifier').toUpperCase()}
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., MP123"
                value={venueId}
                onChange={e => handleVenueIdChange(e.target.value)}
                disabled={isEnabled}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {i18next.t('hotel-mp-property-identifier-hint')}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                {i18next.t('hotel-mp-validated-hotel-name').toUpperCase()}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  px: 1.5,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                {isValidating ? (
                  <CircularProgress size={16} />
                ) : hasSearched && validatedName ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <NoEntryIcon fontSize="small" color="disabled" />
                )}
                <Typography variant="body2" color={validatedName ? 'text.primary' : 'text.secondary'}>
                  {isValidating ? i18next.t('hotel-mp-validating') : validatedName || i18next.t('hotel-mp-no-hotel-found')}
                </Typography>
              </Paper>
              {hasSearched && !validatedName && !isValidating && (
                <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                  {i18next.t('hotel-mp-enter-valid-identifier')}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {isEnabled && (
          <Alert
            severity="info"
            sx={{ mt: 2, alignItems: 'center' }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
                onClick={handleSync}
                disabled={syncing || deleting}
              >
                {i18next.t('hotel-mp-sync-now')}
              </Button>
            }
          >
            {i18next.t('hotel-mp-active-info', { name: hotelName })}
          </Alert>
        )}
      </Paper>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {i18next.t('hotel-mp-need-assistance')}
        </Typography>
        <Typography
          variant="body2"
          component="a"
          href="https://www.seminargo.com/faq"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {i18next.t('hotel-mp-view-documentation')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          |
        </Typography>
        <Typography
          variant="body2"
          component="a"
          href="mailto:office@seminargo.com"
          sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {i18next.t('hotel-mp-contact-support')}
        </Typography>
      </Box>
    </Box>
  );
};

export default HotelMeetingPackageTab;
